'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, Palette, Sun, Moon } from 'lucide-react';

const templates = [
  {
    slug: 'apex',
    name: 'Apex',
    tagline: 'Dark luxury trading platform',
    theme: 'dark',
    bg: '#140A12',
    accent: '#D4526A',
    accentName: 'Crimson Rose',
    preview: 'Elegant dark design with bordeaux tones. Ideal for premium brokers.',
  },
  {
    slug: 'atlas',
    name: 'Atlas',
    tagline: 'Bold red on dark navy',
    theme: 'dark',
    bg: '#1B2033',
    accent: '#E63946',
    accentName: 'Signal Red',
    preview: 'Dark navy with bold red accent. Professional and modern look.',
  },
  {
    slug: 'aurora',
    name: 'Aurora',
    tagline: 'Clean white with sky blue',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#0EA5E9',
    accentName: 'Sky Blue',
    preview: 'Clean, bright design with sky-blue accents. Fresh and trustworthy.',
  },
  {
    slug: 'meridian',
    name: 'Meridian',
    tagline: 'Professional teal on white',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#0D9488',
    accentName: 'Deep Teal',
    preview: 'Clean white with teal green. Conveys professionalism and stability.',
  },
  {
    slug: 'nova',
    name: 'Nova',
    tagline: 'Dark forest green vibes',
    theme: 'dark',
    bg: '#071A14',
    accent: '#22C55E',
    accentName: 'Forest Green',
    preview: 'Deep green dark theme with bright highlights. Evokes growth and positive trading.',
  },
  {
    slug: 'obsidian',
    name: 'Obsidian',
    tagline: 'Minimalist pure black',
    theme: 'dark',
    bg: '#09090B',
    accent: '#FAFAFA',
    accentName: 'Pure White',
    preview: 'Pure black with white text. Maximum contrast, ultra-minimalist style.',
  },
  {
    slug: 'prime',
    name: 'Prime',
    tagline: 'Deep navy blue authority',
    theme: 'dark',
    bg: '#0A1628',
    accent: '#3B82F6',
    accentName: 'Prime Blue',
    preview: 'Midnight blue with bright accents. Conveys trust and financial authority.',
  },
  {
    slug: 'royal',
    name: 'Royal',
    tagline: 'Elegant purple on white',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#7C3AED',
    accentName: 'Royal Purple',
    preview: 'White with royal purple. Sophisticated design for premium clients.',
  },
  {
    slug: 'spectra',
    name: 'Spectra',
    tagline: 'Warm amber dark theme',
    theme: 'dark',
    bg: '#14100A',
    accent: '#F59E0B',
    accentName: 'Amber Gold',
    preview: 'Warm dark theme with amber gold. Rich and premium appearance.',
  },
  {
    slug: 'vertex',
    name: 'Vertex',
    tagline: 'Cyber cyan on white',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#00B8D9',
    accentName: 'Cyber Cyan',
    preview: 'Modern white with tech cyan. Innovative fintech look.',
  },
  {
    slug: 'ytbroker',
    name: 'YTBroker',
    tagline: 'Institutional red on white',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#DC1F3A',
    accentName: 'Signal Red',
    preview:
      'IG-inspired institutional design with deep red accent. Built for serious brokers and trading platforms.',
  },
  {
    slug: 'xtb',
    name: 'XTB',
    tagline: 'Navy & gold ambassador style',
    theme: 'light',
    bg: '#FFFFFF',
    accent: '#F5A623',
    accentName: 'Trophy Gold',
    preview:
      'XTB-inspired premium broker layout with ambassador hero, navy gradient and gold accents. Investor-focused.',
  },
];

export default function TemplatesPage() {
  const darkTemplates = templates.filter((t) => t.theme === 'dark');
  const lightTemplates = templates.filter((t) => t.theme === 'light');

  return (
    <div className="min-h-screen bg-[#0F0F13] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0F0F13]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-[#6C5CE7] flex items-center justify-center">
              <Palette className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
            </div>
            <span className="font-semibold text-base md:text-lg">Template Gallery</span>
          </div>
          <a
            href="/"
            className="text-xs md:text-sm text-white/50 hover:text-white transition-colors"
          >
            ← Back to Vanta
          </a>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#6C5CE7]/10 border border-[#6C5CE7]/30 rounded-full px-4 py-1.5 text-sm text-[#6C5CE7] mb-6">
            <Eye className="w-3.5 h-3.5" />
            12 templates available
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Choose your <span className="text-[#6C5CE7]">template</span>
          </h1>
          <p className="text-white/50 text-sm md:text-lg max-w-2xl mx-auto px-2 md:px-0">
            Each template is a complete landing page for brokers and trading platforms. Fully
            customizable with your brand, colors and content.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-24 md:pb-20">
        {/* Dark templates */}
        <div className="mb-14">
          <div className="flex items-center gap-2 mb-6">
            <Moon className="w-4 h-4 text-white/40" />
            <h2 className="text-white/40 text-sm font-medium uppercase tracking-widest">
              Dark Theme — {darkTemplates.length} templates
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {darkTemplates.map((t, i) => (
              <TemplateCard key={t.slug} template={t} index={i} />
            ))}
          </div>
        </div>

        {/* Light templates */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Sun className="w-4 h-4 text-white/40" />
            <h2 className="text-white/40 text-sm font-medium uppercase tracking-widest">
              Light Theme — {lightTemplates.length} templates
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lightTemplates.map((t, i) => (
              <TemplateCard key={t.slug} template={t} index={i + darkTemplates.length} />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 text-center text-white/30 text-sm">
        Vanta Technologies — Template Gallery
      </div>
    </div>
  );
}

function TemplateCard({ template, index }: { template: (typeof templates)[0]; index: number }) {
  const isLight = template.theme === 'light';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
    >
      {/* Preview area */}
      <div
        className="relative h-48 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: template.bg }}
      >
        {/* Fake browser chrome */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
        </div>

        {/* Fake content blocks */}
        <div className="flex flex-col items-center gap-3 w-full px-10">
          <div
            className="h-3 rounded-full w-3/5 opacity-90"
            style={{ backgroundColor: template.accent }}
          />
          <div
            className="h-2 rounded-full w-4/5 opacity-20"
            style={{ backgroundColor: isLight ? '#000' : '#fff' }}
          />
          <div
            className="h-2 rounded-full w-3/5 opacity-15"
            style={{ backgroundColor: isLight ? '#000' : '#fff' }}
          />
          <div className="flex gap-2 mt-2">
            <div
              className="h-7 w-20 rounded-lg opacity-100"
              style={{ backgroundColor: template.accent }}
            />
            <div
              className="h-7 w-20 rounded-lg opacity-20"
              style={{ backgroundColor: isLight ? '#000' : '#fff' }}
            />
          </div>
        </div>

        {/* Theme badge */}
        <div
          className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full font-medium"
          style={{
            backgroundColor: template.accent + '22',
            color: template.accent,
            border: `1px solid ${template.accent}44`,
          }}
        >
          {template.theme}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Link
            href={`/templates/${template.slug}`}
            className="flex items-center gap-2 bg-white text-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-white/90 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </Link>
        </div>
      </div>

      {/* Info */}
      <div className="bg-[#1A1A22] p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-white">{template.name}</h3>
            <p className="text-white/40 text-xs mt-0.5">{template.tagline}</p>
          </div>
          {/* Color dot */}
          <div
            className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 ring-2 ring-white/10"
            style={{ backgroundColor: template.accent }}
            title={template.accentName}
          />
        </div>
        <p className="text-white/50 text-xs mt-2 leading-relaxed">{template.preview}</p>
        <Link
          href={`/templates/${template.slug}`}
          className="mt-3 flex items-center gap-1 text-xs font-medium transition-colors"
          style={{ color: template.accent }}
        >
          View template
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </motion.div>
  );
}
